import { useEffect, useState } from 'react';
import { Button, Modal, Popconfirm, Progress, Space, Table, Upload, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { ColumnsType } from 'antd/lib/table';
import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../../Contexts/AuthContext';
import { TextService } from '../../Services/TextService';
import { DocumentService } from '../../Services/DocumentService';
import { WebApiDocumentsDtoCompanyDocumentDto } from '../../Repository/eventbookyapi';
import { ResponseError } from '../../Repository/eventbookyapi/runtime';
import { formatDateString } from '../../Helpers/DateHelpers';
import { downloadBlobFile, getFileNameFromContentDisposition } from '../../Helpers/FileDownloadHelper';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenuSettings from '../Account/LeftSettings';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_CONTENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
];

const formatBytes = (bytes: number) => {
    if (!bytes) {
        return '0 MB';
    }
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const Documents = () => {
    const { user } = useAuth();
    const GetText = TextService.GetText;

    const [documents, setDocuments] = useState<WebApiDocumentsDtoCompanyDocumentDto[]>([]);
    const [usedBytes, setUsedBytes] = useState(0);
    const [limitBytes, setLimitBytes] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = async () => {
        setLoading(true);
        try {
            const result = await DocumentService.GetList();
            setDocuments(result.documents || []);
            setUsedBytes(result.usedBytes || 0);
            setLimitBytes(result.limitBytes || 0);
        } catch {
            message.error(GetText('Documents_FailedToLoad') || 'Kunde inte hämta dokument.');
        }
        setLoading(false);
    };

    const doUpload = async (file: File, overwrite: boolean) => {
        setUploading(true);
        try {
            await DocumentService.Upload(file, overwrite);
            message.success(GetText('Documents_UploadSuccess') || 'Dokumentet har laddats upp.');
            await loadDocuments();
        } catch (error) {
            if (error instanceof ResponseError && error.response.status === 409) {
                Modal.confirm({
                    title: GetText('Documents_OverwriteTitle') || 'Filen finns redan',
                    content: GetText('Documents_OverwriteContent') || `En fil med namnet "${file.name}" finns redan. Vill du skriva över den?`,
                    okText: GetText('Documents_OverwriteConfirm') || 'Skriv över',
                    cancelText: GetText('Common_Cancel') || 'Avbryt',
                    onOk: () => doUpload(file, true),
                });
            } else if (error instanceof ResponseError) {
                const text = await error.response.text();
                message.error(text || GetText('Documents_UploadFailed') || 'Kunde inte ladda upp filen.');
            } else {
                message.error(GetText('Documents_UploadFailed') || 'Kunde inte ladda upp filen.');
            }
        }
        setUploading(false);
    };

    const handleBeforeUpload = (file: File) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
            message.error(GetText('Documents_FileTooLarge') || 'Filen är för stor. Max 25 MB per fil.');
            return Upload.LIST_IGNORE;
        }

        if (!ALLOWED_CONTENT_TYPES.includes(file.type)) {
            message.error(GetText('Documents_FileTypeNotAllowed') || 'Filtypen stöds inte.');
            return Upload.LIST_IGNORE;
        }

        doUpload(file, false);
        return false;
    };

    const handleDelete = async (id?: number) => {
        if (!id) {
            return;
        }
        try {
            await DocumentService.Delete(id);
            message.success(GetText('Documents_DeleteSuccess') || 'Dokumentet har tagits bort.');
            await loadDocuments();
        } catch {
            message.error(GetText('Documents_DeleteFailed') || 'Kunde inte ta bort dokumentet.');
        }
    };

    const handleDownload = async (doc: WebApiDocumentsDtoCompanyDocumentDto) => {
        if (!doc.id) {
            return;
        }
        try {
            const response = await DocumentService.Download(doc.id);
            const blob = await response.raw.blob();
            const fileName = getFileNameFromContentDisposition(response.raw.headers.get('content-disposition'), doc.fileName || 'document');
            downloadBlobFile(blob, fileName);
        } catch {
            message.error(GetText('Documents_DownloadFailed') || 'Kunde inte ladda ner filen.');
        }
    };

    const columns: ColumnsType<WebApiDocumentsDtoCompanyDocumentDto> = [
        {
            title: GetText('Documents_ColumnName') || 'Namn',
            dataIndex: 'fileName',
            key: 'fileName',
        },
        {
            title: GetText('Documents_ColumnSize') || 'Storlek',
            dataIndex: 'sizeBytes',
            key: 'sizeBytes',
            render: (sizeBytes: number) => formatBytes(sizeBytes),
        },
        {
            title: GetText('Documents_ColumnUploadedBy') || 'Uppladdad av',
            dataIndex: 'createdByName',
            key: 'createdByName',
        },
        {
            title: GetText('Documents_ColumnUploadedAt') || 'Datum',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt: string) => createdAt ? formatDateString(createdAt) : '',
        },
        {
            title: GetText('MemberList_ActionColumn') || 'Åtgärder',
            key: 'actions',
            render: (_, doc) => (
                <Space size="middle">
                    <DownloadOutlined style={{ fontSize: '18px', cursor: 'pointer' }} onClick={() => handleDownload(doc)} />
                    {!user?.hasOnlyReadRights && (
                        <Popconfirm
                            title={GetText('Common_ConfirmDelete') || 'Är du säker på att du vill ta bort filen?'}
                            onConfirm={() => handleDelete(doc.id)}
                            okText={GetText('Common_Yes') || 'Ja'}
                            cancelText={GetText('Common_No') || 'Nej'}
                        >
                            <DeleteOutlined style={{ fontSize: '18px', color: 'red', cursor: 'pointer' }} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const usagePercent = limitBytes > 0 ? Math.min(100, Math.round((usedBytes / limitBytes) * 100)) : 100;
    const hasDocumentsAccess = user?.subscriptionLevel === SubscriptionLevel.Premium || user?.subscriptionLevel === SubscriptionLevel.Enterprise;

    return (
        <LayoutLeftMenu menu={<LeftMenuSettings selected="documents" />}>
            <Title level={2}>{GetText('TopHeader_Documents') || 'Dokument'}</Title>
            <p>{GetText('Documents_Description') || 'Dokument som endast är synliga och åtkomliga för administratörer i er förening.'}</p>

            {hasDocumentsAccess ? (
                <div style={{ maxWidth: 300, marginBottom: 20 }}>
                    <span style={{ fontSize: '12px', color: '#888' }}>{formatBytes(usedBytes)} / {formatBytes(limitBytes)}</span>
                    <Progress
                        percent={usagePercent}
                        size="small"
                        showInfo={false}
                        status={usagePercent >= 100 ? 'exception' : 'normal'}
                        strokeColor={usagePercent >= 100 ? undefined : '#bfbfbf'}
                    />
                </div>
            ) : (
                <p><i>{GetText('Documents_NotAvailableForSubscription') || 'Dokumentfunktionen ingår inte i er nuvarande abonnemangsnivå.'}</i></p>
            )}

            {!user?.hasOnlyReadRights && hasDocumentsAccess && (
                <Upload
                    showUploadList={false}
                    beforeUpload={handleBeforeUpload}
                    disabled={uploading}
                >
                    <Button icon={<UploadOutlined />} loading={uploading}>
                        {GetText('Documents_UploadHint') || 'Ladda upp dokument'}
                    </Button>
                </Upload>
            )}

            <Table
                style={{ marginTop: 24 }}
                columns={columns}
                dataSource={documents}
                rowKey="id"
                loading={loading}
                pagination={false}
            />
        </LayoutLeftMenu>
    );
};

export default Documents;
